/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import '../styles/VideoCall.less';
import Error from './Common/Error';
import PageNotFound from './Common/PageNotFound';
import HomesightLoading from './Common/HomesightLoading';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { initKeycloak, keycloak } from '@vantiva-sa/homesight_humaui/Services/KeycloakService';
import { getCurrentUser, loginSuccess } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { formatKeycloakUserInfo } from '@vantiva-sa/homesight_humaui/Services/UserService';
import { setApiBaseURL } from '@vantiva-sa/homesight_humaui/Store/apiStore';
import { updateAccessToken } from '@vantiva-sa/homesight_humaui/Store/thunks';
import { onLanguageChange } from '@vantiva-sa/homesight_humaui/Utilities/Translation';
import { WebSocketProvider } from './WebSocket/WebsocketContext';
import {
  getUserInfo,
  storeUserInfo,
  storeSiteIconFiles,
  storeSiteIconManifest,
} from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { storePermissions } from '@vantiva-sa/homesight_humaui/Store/permisssionsStore';
import {
  ConfigSourceKeys,
  NavigationKeys,
  UserTypes,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import UserHubs from './UserHubs/UserHubs';
import Schedule from './Schedule/Schedule';
import Devices from './Devices/Devices';
import EditUserProfile from './UserProfile/EditUserProfile';
import Staff from './Staff/Staff';
import AboutPage from './About/AboutPage';
import Notification from './Notification/Notification';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { useMainContext } from '../MainContext';
import Maintenance from './Common/Maintenance';
import { checkForMaintenance } from './WebSocket/WebsocketHelper';
import useUserInfo from './Hooks/useUserInfo';
import useUpdateUser from './Hooks/useUpdateUser';
import JSZip from 'jszip';
import { AppStorageKeys } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import iconzip from '../../static/icons/homesight_task_icons_v1.zip';
import Groups from './Groups/Groups';
import useConfigSources from './Hooks/useConfigSources';
import IntendedUseStatementModal from './Modals/IntendedUseStatementModal';
import { PassiveHealthProbe } from '../features/diagnostics';

const ENV_VARS = SessionStorage.get(AppStorageKeys.envVars);
const CATEGORY_ICON_S3_BUCKET_LINK = ENV_VARS.CATEGORY_ICON_S3_BUCKET_LINK;
const configKey = ConfigSourceKeys.insight_rules_schema;

let keyc = null;
let t_refreshed = 0;

const AppRoutes = () => {
  const { protocol, hostname } = window.location;
  const hostURL = protocol + '//' + hostname;
  const { menuClickKey } = useMainContext();
  const dispatch = useDispatch();
  const location = useLocation();
  const [token, setToken] = useState(null);
  const [kcError, setKcError] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [authenticated, setAuthenticated] = useState(false);
  const [showIntendedUseModal, setShowIntendedUseModal] = useState(false);
  const [announcement, setAnnouncement] = useState({});
  const trans = useSelector(translator);
  const permissionApi = useSelector((state) => state.apis.permissionApi);
  const maintenanceApi = useSelector((state) => state.apis.maintenanceApi);

  const currentUser = useSelector(getCurrentUser);
  const useruuid = useMemo(() => currentUser?.useruuid, [currentUser]);
  const userInfo = useSelector(getUserInfo(useruuid));
  const { data: userDetails } = useUserInfo({ useruuid });
  const { mutate: updateUser } = useUpdateUser({ useruuid });

  useConfigSources({ configKey, options: { enabled: authenticated } });

  useEffect(() => {
    if (authenticated && userDetails) {
      setShowIntendedUseModal(userDetails.requested_user_action !== false);
    }
  }, [authenticated, userDetails]);

  const isNotificationRoute = useMemo(
    () => location.pathname === '/' + NavigationKeys.notification,
    [location.pathname]
  );

  // Track previous route to detect navigation to notification page
  const prevRouteRef = useRef(location.pathname);
  const hasFetchedPermissionsRef = useRef(false);

  const fetchPermissions = () => {
    const errorCallback = (error) => {
      console.error('Error fetching permissions', error);
    };
    const successCallback = (data) => {
      dispatch(storePermissions(data));
    };

    try {
      permissionApi.listPermission(
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  // Function to refresh the token
  const refreshKeycloakToken = useCallback(async () => {
    if (!keyc) return;

    try {
      const refreshed = await keyc.updateToken(30);
      if (refreshed) {
        t_refreshed = 1;
        setRefreshToken((prev) => prev + 1);

        // Update token in Redux store
        const userInfo = formatKeycloakUserInfo(keyc);
        setToken(userInfo.token);
        dispatch(updateAccessToken(userInfo.token));
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
    }
  }, [dispatch]);

  // Check if we've just navigated to the notification route
  useEffect(() => {
    const prevRoute = prevRouteRef.current;
    prevRouteRef.current = location.pathname;

    // If we've just navigated to the notification route
    if (isNotificationRoute && prevRoute !== location.pathname) {
      // Refresh token on demand
      if (keyc) {
        refreshKeycloakToken();
      }
    }
  }, [location.pathname, isNotificationRoute, refreshKeycloakToken]);

  useEffect(() => {
    async function downloadIcons() {
      try {
        let response;
        let blob = null;
        response = await fetch(CATEGORY_ICON_S3_BUCKET_LINK, { mode: 'no-cors' });
        if (response.ok) {
          blob = await response.blob();
        } else {
          blob = iconzip;
          console.warn('Using local icon zip as S3 fetch failed!');
        }
        const zip = new JSZip();
        const loadedZip = await zip.loadAsync(blob);

        dispatch(storeSiteIconFiles({ files: loadedZip }));

        const manifestFile = loadedZip.file('manifest.json');
        if (!manifestFile) {
          throw new Error('manifest.json not found in zip archive');
        }
        const manifestContent = await manifestFile.async('string');
        const manifest = JSON.parse(manifestContent);
        dispatch(storeSiteIconManifest({ manifest: manifest }));
      } catch (error) {
        console.error('Error downloading or processing zip:', error);
      }
    }
    downloadIcons();
  }, []);

  useEffect(() => {
    const realm = hostname.split('.')[0];
    try {
      keyc = keycloak(realm, hostURL);
      keyc.onTokenExpired = () => {
        keyc
          .updateToken(30)
          .then((refreshed) => {
            if (refreshed) {
              t_refreshed = 1;
              setRefreshToken((prev) => prev + 1);

              // Update token in Redux store
              const userInfo = formatKeycloakUserInfo(keyc);
              setToken(userInfo.token);
              dispatch(updateAccessToken(userInfo.token));
            }
          })
          .catch((error) => {
            console.error('Error getting a new token', error);
            keyc.logout();
          });
      };

      initKeycloak()
        .then((kc) => {
          localStorage.removeItem('providerData');
          setAuthenticated(true);
          // Modal visibility will be determined by useEffect based on requested_user_action
          const userInfo = formatKeycloakUserInfo(kc);
          /* actions for login success */
          setToken(userInfo.token);
          dispatch(setApiBaseURL(hostURL));
          dispatch(updateAccessToken(userInfo.token));
          dispatch(loginSuccess(userInfo));
          dispatch(storeUserInfo(userInfo));
          onLanguageChange();
        })
        .catch((error) => {
          setKcError(true);
        });
    } catch (error) {
      message.error('Keycloak authentication error!');
      console.error('Keycloak authentication error:', error);
      setKcError(true);
    }
  }, []);

  useEffect(() => {
    if (authenticated && !hasFetchedPermissionsRef.current) {
      hasFetchedPermissionsRef.current = true;
      fetchPermissions();
    }
  }, [authenticated]);

  useEffect(() => {
    if (authenticated && userDetails && Object.keys(userDetails).length) {
      checkForMaintenance(userDetails, setAnnouncement, maintenanceApi, trans);
    }
  }, [authenticated, userDetails, maintenanceApi, trans]);

  if (kcError) {
    return <Error message="Login Failed!" description="Error initializing Keycloak." />;
  } else if (!authenticated) {
    return <HomesightLoading />;
  }

  const hasAccess = (userType) => {
    return userType === UserTypes.providerAdmin || userType === UserTypes.staff;
  };

  const handleIntendedUseProceed = () => {
    setShowIntendedUseModal(false);
    updateUser({ useruuid, requested_user_action: false });
  };

  return (
    <WebSocketProvider hostname={hostname} token={token} setAnnouncement={setAnnouncement}>
      <PassiveHealthProbe />
      <IntendedUseStatementModal
        open={showIntendedUseModal}
        onProceed={handleIntendedUseProceed}
        useruuid={useruuid}
      />
      {!!announcement?.message && (
        <Maintenance
          type={announcement?.type}
          message={announcement?.message}
          onClose={() => setAnnouncement({})}
        />
      )}
      <Routes>
        <Route
          exact
          path="/"
          element={
            <div key={menuClickKey}>
              <UserHubs />
            </div>
          }
        />
        <Route
          exact
          path={'/' + NavigationKeys.accounts}
          element={
            <div key={menuClickKey}>
              <UserHubs />
            </div>
          }
        />
        <Route
          exact
          path={'/' + NavigationKeys.schedule}
          element={
            <div key={menuClickKey}>
              <Schedule />
            </div>
          }
        />
        <Route
          exact
          path={'/' + NavigationKeys.groups}
          element={
            hasAccess(userInfo.usertype) ? (
              <div key={menuClickKey}>
                <Groups />
              </div>
            ) : (
              <Navigate replace to="/" />
            )
          }
        />
        <Route
          exact
          path={'/' + NavigationKeys.staff}
          element={
            hasAccess(userInfo.usertype) ? (
              <div key={menuClickKey}>
                <Staff />
              </div>
            ) : (
              <Navigate replace to="/" />
            )
          }
        />
        <Route
          exact
          path={'/' + NavigationKeys.about}
          element={
            <div key={menuClickKey}>
              <AboutPage />
            </div>
          }
        />
        <Route
          exact
          path={'/' + NavigationKeys.devices}
          element={
            hasAccess(userInfo.usertype) ? (
              <div key={menuClickKey}>
                <Devices />
              </div>
            ) : (
              <Navigate replace to="/" />
            )
          }
        />
        <Route
          exact
          path={'/' + NavigationKeys.profile}
          element={
            <div key={menuClickKey}>
              <EditUserProfile />
            </div>
          }
        />
        <Route
          exact
          path={'/' + NavigationKeys.notification}
          element={
            <div key={menuClickKey}>
              <Notification />
            </div>
          }
        />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </WebSocketProvider>
  );
};

export default AppRoutes;
